/*
 * KAS LUXURY — the live metal price, in the header and beside every product price.
 *
 * WHY
 * Benchmarked off traxnyc.com, which carries live gold, silver and platinum across the
 * top of every page with the line "spot - we price by weight". For a shop whose whole
 * claim is "custom made, not custom priced", showing the metal price is the cheapest
 * proof available that the price is built from something real rather than invented.
 *
 * WHERE THE NUMBER COMES FROM, AND WHY NOT GOLDPRICE.ORG
 * The standing instruction is to read gold from goldprice.org. That site cannot be read
 * from a browser on another domain -- its data endpoint sends no CORS header and the
 * fetch fails outright. So the page reads api.gold-api.com, which is CORS-open and needs
 * no key. It was cross-checked before shipping: gold-api returned $4,185.80 per ounce at
 * the same moment traxnyc.com's own live header showed $4,183.97, under two dollars
 * apart. goldprice.org remains the source for anything priced by hand.
 *
 * IT FAILS CLOSED, DELIBERATELY.
 * A wrong gold price on a jewellery site is worse than no gold price. If the request
 * fails, or the figure falls outside a sane band, NOTHING is rendered. There is no
 * hard-coded fallback number anywhere in this file, because a stale figure that looks
 * live is exactly the thing that would damage trust.
 *
 * IT MUST BE APPENDED INSIDE .announcement-bar, NOT INTO THE SECTION AROUND IT.
 * That bar is position:relative with z-index:510 and an opaque background. The first
 * version of this file appended to the surrounding .shopify-section, so the bar painted
 * straight over the strip: the element measured correct at 1010x11, 391 by 13, with
 * opacity 1 -- and was invisible on screen. Measuring the box is not the same as seeing
 * it. Keep the append target as the bar itself.
 *
 * WHERE IT GOES
 *   - the announcement bar, absolutely positioned at the right, adding NO height. The
 *     sticky header offset comes from --announcement-height, and a taller bar would
 *     push the header out of line with it.
 *   - under the price on every product page, at every width. That is where it actually
 *     argues for the price, and the header strip has no room on a phone.
 *
 * NO BACKSLASHES IN THIS FILE -- see the note in kas-ring-size.js.
 */
(function () {
  'use strict';

  var GOLD = 'https://api.gold-api.com/price/XAU';
  var PLATINUM = 'https://api.gold-api.com/price/XPT';
  var GRAMS_PER_TROY_OUNCE = 31.1034768;
  var CACHE_KEY = 'kas-metal-spot';
  var CACHE_MINUTES = 15;

  var GOLD_SANE = [800, 25000];
  var PLATINUM_SANE = [200, 12000];

  function sane(value, bounds) {
    return typeof value === 'number' && isFinite(value) &&
           value >= bounds[0] && value <= bounds[1];
  }

  function perGram(perOunce) {
    return perOunce / GRAMS_PER_TROY_OUNCE;
  }

  function dollars(n) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.at) return null;
      if (Date.now() - o.at > CACHE_MINUTES * 60000) return null;
      return o;
    } catch (e) {
      return null;
    }
  }

  function writeCache(o) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(o));
    } catch (e) { /* storage is a convenience, never a requirement */ }
  }

  function fetchOne(url) {
    return fetch(url, { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return d && typeof d.price === 'number' ? d.price : null; })
      .catch(function () { return null; });
  }

  function getSpot() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);
    return Promise.all([fetchOne(GOLD), fetchOne(PLATINUM)]).then(function (r) {
      var gold = sane(r[0], GOLD_SANE) ? r[0] : null;
      var plat = sane(r[1], PLATINUM_SANE) ? r[1] : null;
      if (gold === null) return null;
      var out = { gold: gold, platinum: plat, at: Date.now() };
      writeCache(out);
      return out;
    });
  }

  function label(spot, withPlatinum) {
    var parts = ['Gold ' + dollars(perGram(spot.gold)) + '/g'];
    if (withPlatinum && spot.platinum !== null) {
      parts.push('Platinum ' + dollars(perGram(spot.platinum)) + '/g');
    }
    return parts.join('   ·   ');
  }

  function intoHeader(spot) {
    var bar = document.querySelector('.announcement-bar');
    if (!bar || document.getElementById('kas-spot-header')) return;

    var el = document.createElement('div');
    el.id = 'kas-spot-header';
    el.className = 'kas-spot kas-spot--header';
    el.innerHTML = '<span class="kas-spot__figures"></span>' +
                   '<span class="kas-spot__note">live spot, we price by weight</span>';
    el.querySelector('.kas-spot__figures').textContent = label(spot, true);
    el.setAttribute('title', 'Gold ' + dollars(spot.gold) + ' per troy ounce' +
      (spot.platinum !== null ? ', platinum ' + dollars(spot.platinum) + ' per troy ounce' : ''));

    bar.appendChild(el);          // INSIDE the bar. See the note at the top of this file.
  }

  function intoProduct(spot) {
    if (document.getElementById('kas-spot-product')) return;
    var price = document.querySelector('.product__price');
    if (!price) return;
    var el = document.createElement('p');
    el.id = 'kas-spot-product';
    el.className = 'kas-spot kas-spot--product';
    el.textContent = label(spot, true) + '  ·  today';
    price.parentNode.insertBefore(el, price.nextSibling);
  }

  function start() {
    getSpot().then(function (spot) {
      if (!spot) return;
      intoHeader(spot);
      intoProduct(spot);
    }).catch(function () { /* never break a page over a decoration */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());